import React, { useEffect, useState } from "react";
import BaseModalAnimated from "../../components/modal/BaseModalAnimated";
import { useFormik } from "formik";
import * as Yup from "yup";
import { showFormErrors } from "../../helpers/showFormErrors";
import CustomInput from "../../components/form/CustomInput";
import { useSelector } from "react-redux";
import CustomButton from "../../components/form/CustomButton";
import {
    getPaymentsMethod,
    getPrefixes,
    getSupplierCompanies,
    getSupplierTypes,
    suppliersResource,
} from "../../helpers/configRoutes/backend_helper";
import CustomSelect from "../../components/form/CustomSelect";

const AddUpSupplier = ({ modal, setModal, setFilters, filters }) => {
    const [newCompany, setNewCompany] = useState(false);
    const { supplier, supplierValidations } = useSelector((state) => state.Supplier);
    const [loading, setLoading] = useState(false);
    
    // Catalogos
    const [companies, setCompanies] = useState([]);
    const [types, setTypes] = useState([]);
    const [methods, setMethods] = useState([]);
    const [prefixes, setPrefixes] = useState([]);

    useEffect(() => {
        const Data = async () => {
            try {
                const companiesResponse = await getSupplierCompanies("get", {});
                setCompanies(companiesResponse.companies || []);

                const supplierTypeResponse = await getSupplierTypes("get", {});
                setTypes(supplierTypeResponse.types || []);

                const paymentsMethodResponse = await getPaymentsMethod("get", {});
                setMethods(paymentsMethodResponse.methods || []);

                const prefixesResponse = await getPrefixes("get", {});
                setPrefixes(prefixesResponse.prefixes || []);
            } catch (error) {
                console.error("Error fetching supplier data:", error);
            }
        };

        Data();
    }, []);

    const validation = useFormik({
        initialValues: supplier,
        enableReinitialize: true, // Importante si supplier viene de redux
        validationSchema: Yup.object(supplierValidations),
        onSubmit: async (values, { setErrors }) => {
            setLoading(true);
            const response = await suppliersResource(
                values.id ? "put" : "post",
                values,
                values.id
            );
            if (response.status) {
                setFilters({ ...filters, search: filters.search });
                setModal({ ...modal, show: false });
            } else {
                setErrors(response.errors);
            }
            setLoading(false);
        },
    });

    // Componente visual para separar secciones
    const SectionDivider = ({ label }) => (
        <div className="flex items-center gap-3 my-5">
            <div className="h-px bg-gray-200 flex-1"></div>
            <span className="text-[10px] uppercase font-bold text-gray-400 tracking-widest">{label}</span>
            <div className="h-px bg-gray-200 flex-1"></div>
        </div>
    );

    return (
        <BaseModalAnimated 
            modal={modal} 
            setModal={setModal}
            size="max-w-3xl"
            title={validation.values.id ? "Edit Supplier" : "Add New Supplier"}
        >
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    validation.handleSubmit();
                    showFormErrors(validation);
                    return false;
                }}
                className="flex flex-col h-full"
            >
                {/* --- BODY --- */}
                <div className="p-1 overflow-y-auto custom-scrollbar bg-gray-50/50">
                    
                    {/* SECTION 1: CONTACT INFO */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 ">
                        <CustomInput
                            label="Contact Name"
                            type="text"
                            name="name"
                            placeholder="Full name"
                            labelClassName="text-xs font-semibold text-gray-600"
                            error={validation.errors?.name}
                            value={validation.values?.name}
                            handleChange={validation.handleChange}
                        />
                        <CustomInput
                            label="Email Address"
                            type="email"
                            name="email"
                            placeholder="contact@supplier.com"
                            labelClassName="text-xs font-semibold text-gray-600"
                            error={validation.errors?.email}
                            value={validation.values?.email}
                            handleChange={validation.handleChange}
                        />
                    </div>

                    <SectionDivider label="Company Details" />

                    {/* SECTION 2: COMPANY SELECTION */}
                    <div className="">
                        <div className="flex justify-between items-end ">
                            <label className="text-xs font-semibold text-gray-600">Company</label>
                            
                            {/* Toggle New Company */}
                            {!validation.values.newCompany ? (
                                <button
                                    type="button"
                                    onClick={() => {
                                        validation.setFieldValue("newCompany", true);
                                        setNewCompany(true);
                                    }}
                                    className="text-[10px] text-cs-primary font-bold hover:underline cursor-pointer flex items-center gap-1"
                                >
                                    + Create New Company
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => {
                                        validation.setFieldValue("newCompany", false);
                                        setNewCompany(false);
                                    }}
                                    className="text-[10px] text-red-500 font-bold hover:underline cursor-pointer"
                                >
                                    Cancel Creation (Select Existing)
                                </button>
                            )}
                        </div>

                        <CustomSelect
                            options={companies}
                            name="company_supplier_id"
                            noLabel={true}
                            disabled={validation.values.newCompany}
                            className={`w-full ${validation.values.newCompany ? 'opacity-50' : ''}`}
                            error={validation.errors?.company_supplier_id}
                            value={validation.values?.company_supplier_id}
                            handleChange={validation.handleChange}
                            placeholder="Select a company..."
                        />
                    </div>

                    {/* NEW COMPANY FORM FIELDS (Conditional) */}
                    {validation.values.newCompany && (
                        <div className="bg-blue-50/60 p-2 rounded-xl border border-blue-100  animate-fadeIn">
                            <h5 className="text-[10px] uppercase font-bold text-blue-800 mb-1">New Company Registration</h5>
                            <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                                <CustomInput
                                    label="Company Name"
                                    type="text"
                                    name="name_company"
                                    placeholder="Enter business name"
                                    labelClassName="text-xs font-semibold text-blue-800 "
                                    className="bg-white border-blue-200"
                                    error={validation.errors?.name_company}
                                    value={validation.values?.name_company}
                                    handleChange={validation.handleChange}
                                />
                                <CustomInput
                                    label="Address"
                                    type="text"
                                    name="address"
                                    placeholder="Business address"
                                    labelClassName="text-xs font-semibold text-blue-800 "
                                    className="bg-white border-blue-200"
                                    error={validation.errors?.address}
                                    value={validation.values?.address}
                                    handleChange={validation.handleChange}
                                />
                            </div>
                        </div>
                    )}

                    {/* PHONE */}
                    <div>
                         <label className="text-xs font-semibold text-gray-600  block">Phone Number</label>
                         <div className="flex gap-2">
                            <div className="w-28">
                                <CustomSelect
                                    name="prefix_id"
                                    options={prefixes}
                                    noLabel={true}
                                    className="text-sm"
                                    error={validation.errors?.prefix_id}
                                    value={validation.values?.prefix_id}
                                    handleChange={validation.handleChange}
                                />
                            </div>
                            <div className="flex-1">
                                <CustomInput
                                    type="text"
                                    name="phone"
                                    placeholder="000-000-0000"
                                    noLabel={true}
                                    error={validation.errors?.phone}
                                    value={validation.values?.phone}
                                    handleChange={validation.handleChange}
                                />
                            </div>
                        </div>
                    </div>

                    <SectionDivider label="Service & Payment" />

                    {/* SECTION 3: BUSINESS LOGIC */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <CustomSelect
                            label="Service Type"
                            name="supplier_type_id"
                            options={types}
                            labelClassName="text-xs font-semibold text-gray-600 "
                            error={validation.errors?.supplier_type_id}
                            value={validation.values?.supplier_type_id}
                            handleChange={validation.handleChange}
                            placeholder="Select Type..."
                        />
                        <CustomSelect
                            label="Payment Method"
                            name="payment_method_id"
                            options={methods}
                            labelClassName="text-xs font-semibold text-gray-600 "
                            error={validation.errors?.payment_method_id}
                            value={validation.values?.payment_method_id}
                            handleChange={validation.handleChange}
                            placeholder="Select Method..."
                        />
                    </div>

                </div>

                {/* --- FOOTER --- */}
                <div className="px-6 py-4 bg-white border-t border-gray-100 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={() => setModal({ ...modal, show: false })}
                        className="
                            px-4 py-2 rounded-xl
                            text-xs font-medium
                            text-gray-500
                            hover:bg-gray-50
                            border border-transparent hover:border-gray-200
                            transition-all
                        "
                    >
                        Cancel
                    </button>
                    <CustomButton
                        loading={loading}
                        textLoad="Saving..."
                        type="submit"
                        className="
                            px-6 py-2 rounded-xl
                            bg-cs-primary hover:bg-cs-primary-hover
                            text-white text-xs font-bold
                            shadow-md hover:shadow-lg
                            transition-all
                        "
                    >
                        {validation.values.id ? "Save Changes" : "Create Supplier"}
                    </CustomButton>
                </div>

            </form>
        </BaseModalAnimated>
    );
};

export default AddUpSupplier;